import React, { useState, useEffect } from 'react';
import {
  ShieldAlert,
  AlertTriangle,
  CheckCircle2,
  Calendar,
  Activity,
  Eye,
  Lock,
  Search,
  X,
  ChevronLeft,
  ChevronRight,
  Stethoscope,
  Info
} from 'lucide-react';
import { AIFraudCasesResponse } from '../types/dashboard';
import { formatCurrency } from '../utils/formatters';

interface FraudCasesProps {
  fraudCases: AIFraudCasesResponse | null;
}

const FraudCases: React.FC<FraudCasesProps> = ({ fraudCases }) => {
  const [selectedCase, setSelectedCase] = useState<number | null>(null);
  const [filterRisk, setFilterRisk] = useState<'all' | 'high' | 'medium' | 'low'>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(6);

  // Close modal on ESC key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsModalOpen(false);
      }
    };
    if (isModalOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filterRisk, rowsPerPage]);

  const openModal = (idx: number) => {
    setSelectedCase(idx);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedCase(null), 250);
  };

  if (!fraudCases || !fraudCases.fraud_cases || fraudCases.fraud_cases.length === 0) {
    return (
      <div className="text-center py-20 bg-card rounded-2xl border border-border shadow-sm">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-emerald-50 text-emerald-600 rounded-3xl mb-6 border border-emerald-100 shadow-sm">
          <CheckCircle2 className="w-10 h-10" />
        </div>
        <h3 className="text-2xl font-bold text-foreground mb-3">All Clear!</h3>
        <p className="text-sm text-muted-foreground max-w-md mx-auto mb-8 leading-relaxed">
          No high-priority fraud cases detected. All monitored claims are within normal compliance parameters.
        </p>
        <div className="inline-flex items-center gap-3 px-5 py-3 bg-emerald-50/80 rounded-xl border border-emerald-200">
          <div className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
          </div>
          <span className="text-xs font-semibold text-emerald-800 tracking-wide uppercase">AI Active Sentinel Running</span>
        </div>
      </div>
    );
  }

  const getRiskLevel = (score: number): 'high' | 'medium' | 'low' => {
    if (score >= 0.7) return 'high';
    if (score >= 0.5) return 'medium';
    return 'low';
  };

  const getRiskConfig = (level: 'high' | 'medium' | 'low') => {
    const configs = {
      high: {
        gradient: 'from-rose-600 via-rose-700 to-rose-900',
        badgeBg: 'bg-rose-50 border-rose-200 text-rose-800',
        pillBg: 'bg-rose-600 text-white',
        borderClass: 'border-rose-200/80 hover:border-rose-400',
        glowClass: 'shadow-rose-100',
        scoreColor: 'text-rose-600',
        label: 'Critical Risk',
        priority: 'Immediate Hold',
        icon: <ShieldAlert className="w-5 h-5 text-white" />
      },
      medium: {
        gradient: 'from-amber-500 via-amber-600 to-amber-700',
        badgeBg: 'bg-amber-50 border-amber-200 text-amber-800',
        pillBg: 'bg-amber-600 text-white',
        borderClass: 'border-amber-200/80 hover:border-amber-400',
        glowClass: 'shadow-amber-100',
        scoreColor: 'text-amber-600',
        label: 'Warning',
        priority: 'Review Required',
        icon: <AlertTriangle className="w-5 h-5 text-white" />
      },
      low: {
        gradient: 'from-primary-600 via-primary-700 to-indigo-800',
        badgeBg: 'bg-blue-50 border-blue-200 text-blue-800',
        pillBg: 'bg-primary-600 text-white',
        borderClass: 'border-blue-200/80 hover:border-blue-400',
        glowClass: 'shadow-blue-100',
        scoreColor: 'text-primary-600',
        label: 'Routine Monitor',
        priority: 'Periodic Audit',
        icon: <Activity className="w-5 h-5 text-white" />
      }
    };
    return configs[level];
  };

  const filteredCases = fraudCases.fraud_cases.filter(c => {
    if (filterRisk === 'all') return true;
    return getRiskLevel(c.fraud_score) === filterRisk;
  });

  const riskCounts = {
    high: fraudCases.fraud_cases.filter(c => getRiskLevel(c.fraud_score) === 'high').length,
    medium: fraudCases.fraud_cases.filter(c => getRiskLevel(c.fraud_score) === 'medium').length,
    low: fraudCases.fraud_cases.filter(c => getRiskLevel(c.fraud_score) === 'low').length,
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredCases.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentCases = filteredCases.slice(startIndex, endIndex);

  return (
    <div className="space-y-6">
      {/* Top Banner with Glassmorphism and Status Metrics */}
      <div className="glass-panel p-6 rounded-2xl border border-border shadow-sm bg-gradient-to-r from-card via-card to-primary-50/20">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-600 to-primary-800 flex items-center justify-center text-white shadow-md shadow-primary-500/20">
              <ShieldAlert className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold text-foreground">AI Fraud Case Queue</h2>
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-primary-50 text-primary-700 border border-primary-200">
                  Live Classifier
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                Automated probabilistic scoring and heuristic fraud detection
              </p>
            </div>
          </div>

          <div className="flex items-center gap-6 divide-x divide-border">
            <div className="text-center px-4">
              <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider mb-0.5">Analyzed</p>
              <p className="text-2xl font-extrabold font-mono text-foreground">{fraudCases.total_analyzed.toLocaleString()}</p>
            </div>
            <div className="text-center pl-6 pr-4">
              <p className="text-[11px] font-bold text-rose-600 uppercase tracking-wider mb-0.5">Flagged High Risk</p>
              <p className="text-2xl font-extrabold font-mono text-rose-600">{fraudCases.total_flagged.toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter and View Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-card p-3 rounded-2xl border border-border shadow-sm">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider px-2">Risk Level:</span>
          
          <button
            onClick={() => setFilterRisk('all')}
            className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-2 ${
              filterRisk === 'all'
                ? 'bg-primary-600 text-white shadow-md shadow-primary-600/25'
                : 'bg-muted text-muted-foreground hover:bg-slate-200 hover:text-foreground'
            }`}
          >
            <span>All Cases</span>
            <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-white/20">{fraudCases.fraud_cases.length}</span>
          </button>

          <button
            onClick={() => setFilterRisk('high')}
            className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-2 ${
              filterRisk === 'high'
                ? 'bg-rose-600 text-white shadow-md shadow-rose-600/25'
                : 'bg-rose-50 text-rose-800 hover:bg-rose-100 border border-rose-200'
            }`}
          >
            <ShieldAlert className="w-3.5 h-3.5" />
            <span>Critical</span>
            <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-rose-200 text-rose-900">{riskCounts.high}</span>
          </button>

          <button
            onClick={() => setFilterRisk('medium')}
            className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-2 ${
              filterRisk === 'medium'
                ? 'bg-amber-600 text-white shadow-md shadow-amber-600/25'
                : 'bg-amber-50 text-amber-800 hover:bg-amber-100 border border-amber-200'
            }`}
          >
            <AlertTriangle className="w-3.5 h-3.5" />
            <span>Warning</span>
            <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-amber-200 text-amber-900">{riskCounts.medium}</span>
          </button>

          <button
            onClick={() => setFilterRisk('low')}
            className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-2 ${
              filterRisk === 'low'
                ? 'bg-primary-700 text-white shadow-md shadow-primary-700/25'
                : 'bg-blue-50 text-blue-800 hover:bg-blue-100 border border-blue-200'
            }`}
          >
            <Activity className="w-3.5 h-3.5" />
            <span>Monitor</span>
            <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-blue-200 text-blue-900">{riskCounts.low}</span>
          </button>
        </div>

        {/* Page size & info */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground font-medium">Show:</span>
            <select
              value={rowsPerPage}
              onChange={(e) => setRowsPerPage(Number(e.target.value))}
              className="px-2.5 py-1.5 bg-muted border border-border rounded-lg text-xs font-bold text-foreground focus:outline-none focus:ring-2 focus:ring-primary-500 cursor-pointer"
            >
              <option value={3}>3 / page</option>
              <option value={6}>6 / page</option>
              <option value={9}>9 / page</option>
              <option value={12}>12 / page</option>
            </select>
          </div>
          <span className="text-xs font-semibold text-muted-foreground border-l border-border pl-3">
            Page {currentPage} of {Math.max(1, totalPages)}
          </span>
        </div>
      </div>

      {/* Grid of Case Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {currentCases.map((fraudCase, idx) => {
          const riskLevel = getRiskLevel(fraudCase.fraud_score);
          const config = getRiskConfig(riskLevel);
          const originalIndex = startIndex + idx;
          const scorePercent = (fraudCase.fraud_score * 100).toFixed(0);

          return (
            <div
              key={originalIndex}
              className={`group bg-card rounded-2xl border ${config.borderClass} overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col justify-between`}
            >
              {/* Card Header Gradient */}
              <div className={`bg-gradient-to-br ${config.gradient} p-4 text-white relative overflow-hidden`}>
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-12 -mt-12 pointer-events-none" />
                <div className="flex items-start justify-between relative z-10">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-lg bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30">
                      {config.icon}
                    </div>
                    <div>
                      <span className="text-[10px] font-extrabold uppercase tracking-wider text-white/80">{config.label}</span>
                      <p className="text-base font-extrabold font-mono text-white">#{fraudCase.member_id}</p>
                    </div>
                  </div>

                  <div className="bg-white/20 backdrop-blur-md px-3 py-1.5 rounded-xl border border-white/30 text-right">
                    <p className="text-[10px] uppercase font-bold text-white/80">AI Risk</p>
                    <p className="text-lg font-black font-mono leading-none">{scorePercent}%</p>
                  </div>
                </div>

                {/* Score Progress Bar */}
                <div className="mt-3 w-full bg-white/20 h-1.5 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-white rounded-full transition-all duration-500"
                    style={{ width: `${scorePercent}%` }}
                  />
                </div>
              </div>

              {/* Card Body */}
              <div className="p-4 space-y-3.5 flex-1">
                <div className="flex items-center justify-between text-xs text-muted-foreground border-b border-border/60 pb-2.5">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" />
                    <span>{fraudCase.service_date}</span>
                  </div>
                  <span className="font-mono text-[11px] bg-muted px-2 py-0.5 rounded text-foreground">
                    Inv #{fraudCase.invoice_no}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2.5">
                  <div className="bg-muted/40 p-2.5 rounded-xl border border-border/50">
                    <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wide block">Specialty</span>
                    <p className="text-xs font-bold text-foreground truncate mt-0.5" title={fraudCase.specialty_name}>
                      {fraudCase.specialty_name}
                    </p>
                  </div>
                  <div className="bg-muted/40 p-2.5 rounded-xl border border-border/50">
                    <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wide block">Billed Amount</span>
                    <p className="text-xs font-black font-mono text-foreground mt-0.5">
                      {formatCurrency(fraudCase.gross_claim_amount)}
                    </p>
                  </div>
                </div>

                <div>
                  <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider block mb-1.5">
                    Triggered Indicators ({fraudCase.fraud_reasons.length})
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {fraudCase.fraud_reasons.slice(0, 2).map((reason, i) => (
                      <span
                        key={i}
                        className={`text-[11px] font-semibold px-2 py-0.5 rounded-md border ${config.badgeBg} truncate max-w-full`}
                        title={reason}
                      >
                        {reason}
                      </span>
                    ))}
                    {fraudCase.fraud_reasons.length > 2 && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-muted text-muted-foreground border border-border">
                        +{fraudCase.fraud_reasons.length - 2} more
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Card Footer Button */}
              <div className="p-4 pt-0">
                <button
                  onClick={() => openModal(originalIndex)}
                  className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-xs font-bold bg-muted hover:bg-primary-600 hover:text-white text-foreground transition-all duration-200 cursor-pointer group-hover:bg-primary-600 group-hover:text-white shadow-sm"
                >
                  <Eye className="w-3.5 h-3.5" />
                  <span>Inspect Audit File</span>
                  <ChevronRight className="w-3.5 h-3.5 opacity-60" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty Filter State */}
      {filteredCases.length === 0 && (
        <div className="text-center py-16 bg-card rounded-2xl border border-border p-8">
          <Search className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-40" />
          <h3 className="text-base font-bold text-foreground">No cases found in this category</h3>
          <p className="text-xs text-muted-foreground mt-1 mb-4">No cases currently match the selected risk filter.</p>
          <button
            onClick={() => setFilterRisk('all')}
            className="px-4 py-2 bg-primary-600 text-white rounded-xl text-xs font-bold hover:bg-primary-700 transition cursor-pointer"
          >
            Reset Filters
          </button>
        </div>
      )}

      {/* Pagination Controls */}
      {filteredCases.length > 0 && totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 bg-card rounded-2xl border border-border p-3 shadow-sm">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1 ${
              currentPage === 1
                ? 'text-muted-foreground/40 cursor-not-allowed'
                : 'text-foreground hover:bg-muted cursor-pointer'
            }`}
          >
            <ChevronLeft className="w-4 h-4" />
            <span>Previous</span>
          </button>

          <div className="flex items-center gap-1 px-2">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-8 h-8 rounded-lg text-xs font-bold transition cursor-pointer ${
                  currentPage === page
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                {page}
              </button>
            ))}
          </div>

          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1 ${
              currentPage === totalPages
                ? 'text-muted-foreground/40 cursor-not-allowed'
                : 'text-foreground hover:bg-muted cursor-pointer'
            }`}
          >
            <span>Next</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Modal Dialog */}
      {isModalOpen && selectedCase !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div
            className="bg-card w-full max-w-3xl rounded-2xl border border-border shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {(() => {
              const fraudCase = filteredCases[selectedCase];
              const riskLevel = getRiskLevel(fraudCase.fraud_score);
              const config = getRiskConfig(riskLevel);

              return (
                <>
                  {/* Modal Header */}
                  <div className={`bg-gradient-to-r ${config.gradient} p-6 text-white relative`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30">
                          {config.icon}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="px-2.5 py-0.5 rounded-full text-xs font-extrabold bg-white/20 text-white uppercase tracking-wider">
                              {config.label}
                            </span>
                            <span className="text-xl font-extrabold font-mono">
                              {(fraudCase.fraud_score * 100).toFixed(1)}% Score
                            </span>
                          </div>
                          <h3 className="text-lg font-bold mt-1 text-white">Member #{fraudCase.member_id} Audit Report</h3>
                        </div>
                      </div>

                      <button
                        onClick={closeModal}
                        className="w-9 h-9 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center transition cursor-pointer"
                      >
                        <X className="w-5 h-5 text-white" />
                      </button>
                    </div>
                  </div>

                  {/* Modal Body */}
                  <div className="p-6 overflow-y-auto space-y-6 flex-1">
                    {/* Information Bento */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="bg-muted/40 p-3 rounded-xl border border-border/60">
                        <span className="text-[10px] font-bold text-muted-foreground uppercase block">Service Date</span>
                        <p className="text-sm font-bold text-foreground font-mono mt-0.5">{fraudCase.service_date}</p>
                      </div>
                      <div className="bg-muted/40 p-3 rounded-xl border border-border/60">
                        <span className="text-[10px] font-bold text-muted-foreground uppercase block">Invoice No</span>
                        <p className="text-sm font-bold text-foreground font-mono mt-0.5">{fraudCase.invoice_no}</p>
                      </div>
                      <div className="bg-muted/40 p-3 rounded-xl border border-border/60">
                        <span className="text-[10px] font-bold text-muted-foreground uppercase block">Claim Amount</span>
                        <p className="text-sm font-black text-primary-600 font-mono mt-0.5">
                          {formatCurrency(fraudCase.gross_claim_amount)}
                        </p>
                      </div>
                      <div className="bg-muted/40 p-3 rounded-xl border border-border/60">
                        <span className="text-[10px] font-bold text-muted-foreground uppercase block">Quantity</span>
                        <p className="text-sm font-black text-foreground font-mono mt-0.5">{fraudCase.quantity}</p>
                      </div>
                    </div>

                    {/* Medical Details */}
                    <div className="bg-muted/30 p-4 rounded-xl border border-border">
                      <h4 className="text-xs font-bold text-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                        <Stethoscope className="w-4 h-4 text-primary-600" />
                        Clinical Specifications
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                          <span className="text-[11px] text-muted-foreground block">Specialty</span>
                          <span className="text-xs font-bold text-foreground">{fraudCase.specialty_name}</span>
                        </div>
                        <div>
                          <span className="text-[11px] text-muted-foreground block">Treating Physician</span>
                          <span className="text-xs font-bold text-foreground">
                            {fraudCase.treating_physician ? `Dr. ${fraudCase.treating_physician}` : 'Unspecified'}
                          </span>
                        </div>
                        <div>
                          <span className="text-[11px] text-muted-foreground block">Primary Diagnosis</span>
                          <span className="text-xs font-mono font-bold text-foreground">
                            {fraudCase.primary_diag_code || 'None'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Detection Flags */}
                    <div>
                      <h4 className="text-xs font-bold text-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                        <ShieldAlert className="w-4 h-4 text-rose-600" />
                        AI Rule Matches & Anomalies ({fraudCase.fraud_reasons.length})
                      </h4>
                      <div className="space-y-2">
                        {fraudCase.fraud_reasons.map((reason, i) => (
                          <div key={i} className="flex items-start gap-3 p-3 bg-muted/40 rounded-xl border border-border/60">
                            <span className="w-5 h-5 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">
                              {i + 1}
                            </span>
                            <div>
                              <p className="text-xs font-bold text-foreground">{reason}</p>
                              <p className="text-[11px] text-muted-foreground mt-0.5">
                                {reason.toLowerCase().includes('duplicate') ? 'Exact or near-duplicate claim record found for identical patient and service date.' :
                                 reason.toLowerCase().includes('logic') ? 'Service code in direct conflict with medical necessity or standard coding constraints.' :
                                 reason.toLowerCase().includes('quantity') ? 'Procedure frequency exceeds maximum permissible clinical guidelines.' :
                                 'Statistical outlier detected via isolation forest and scoring models.'}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Recommendation */}
                    <div className="p-4 rounded-xl bg-primary-50/60 border border-primary-200">
                      <div className="flex items-start gap-3">
                        <Info className="w-5 h-5 text-primary-600 shrink-0 mt-0.5" />
                        <div>
                          <h5 className="text-xs font-bold text-primary-900 uppercase tracking-wide">Recommended Next Step</h5>
                          <p className="text-xs text-primary-800 mt-1 font-medium leading-relaxed">
                            {fraudCase.recommended_action}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Modal Footer */}
                  <div className="p-4 bg-muted/50 border-t border-border flex items-center justify-end gap-3">
                    <button
                      onClick={closeModal}
                      className="px-4 py-2 rounded-xl text-xs font-bold bg-card border border-border text-foreground hover:bg-muted transition cursor-pointer"
                    >
                      Dismiss
                    </button>
                    <button
                      onClick={closeModal}
                      className="px-5 py-2 rounded-xl text-xs font-bold bg-rose-600 text-white hover:bg-rose-700 transition cursor-pointer flex items-center gap-2 shadow-sm"
                    >
                      <Lock className="w-3.5 h-3.5" />
                      <span>Place Hold on Claim</span>
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default FraudCases;